import React from 'react';
import 'twin.macro';
import { StaticImage } from 'gatsby-plugin-image';

import Layout from '~components/Layout';
import SEO from '~components/SEO';
import Container from '~components/Container';
import Hero from '~components/Hero';
import TableOfContents from '~components/TableOfContents';
import Heading from '~components/Heading';
import Text from '~components/Text';
import Callout from '~components/Callout';
import OverlapImage, { overlapImageStyles } from '~components/OverlapImage';
import Blockquote from '~components/Blockquote';
import FadedImageText from '~components/FadedImageText';

import Fist from '~components/Svg/Fist';
import Group from '~components/Svg/Group';

import { fsbgimg } from '~theme';

const toc = [
  {
    id: 'enhanced-game-changes',
    title: 'Enhanced Game Changers'
  },
  {
    id: 'youth-outreach-worker',
    title: 'Youth Outreach Worker'
  }
];

const ChildrenYouthPage: React.FC = () => (
  <Layout>
    <SEO title="Children & Youth" desc="" />
    <Hero title="Children & Youth">
      <StaticImage src="../images/women-outside-in-red.jpg" alt="" style={fsbgimg} />
    </Hero>
    <Container>
      <TableOfContents items={toc} />
      <Heading id={toc[0].id} level="h2">
        <span tw="text-gray-900">Enhanced</span> Game Changers
      </Heading>
      <Heading level="h3">Calling the game</Heading>
      <Callout>
        <Text tw="text-2xl my-0">
          Francisco’s phone call to Enhanced Game Changers saved his life
        </Text>
      </Callout>
      <Text>
        When a spray of gunshots injured his sister, Francisco knew he had to stop the violence
        pervading his life. A quick call to our Enhanced Game Changers team was the start.
      </Text>
      <Text>
        The Enhanced Game Changers program helps racialized youth who live with the constant threat
        of violence: street, family, sexual or cyber. Peer youth workers on the team work beyond
        9:00 to 5:00, responding to immediate outbreaks of violence with conflict coaching,
        de-escalation, emergency planning, trauma counselling and referrals to legal support when
        necessary. And the work doesn’t stop there: the team provides workshops, referrals for
        mental and physical health issues, and employment assistance. The support goes beyond any
        single incident and focuses on addressing the root causes of the cycle of violence.
      </Text>
      <Text>
        In 2020, the Enhanced Game Changers program expanded services in Oakwood Village by
        establishing a working relationship with the Oakwood Collegiate high school. The private,
        youth-friendly space is managed by Enhanced Game Changers staff with lived experience of the
        systemic violence in the area, and the location improves response time and access for youth
        in the school, and in the community.
      </Text>
      <Text>
        That access saved Francisco. His late night call brought the Enhanced Game Changers team to
        his side. They spoke throughout the night, providing immediate support for his family, and
        for himself the next day when police arrested him for his part in the violence. The team
        supported him through the legal process, offered referrals for trauma counselling, and
        today, provides ongoing support to help him navigate his way out of a life of violence.
      </Text>
    </Container>
    <OverlapImage
      image={
        <StaticImage
          src="../images/enhanced-game-changer-map.jpg"
          alt=""
          style={overlapImageStyles}
        />
      }
    >
      <div tw="flex flex-col md:flex-row items-center space-x-4">
        <Fist tw="w-24 h-24 md:(w-48 h-48) my-6 md:my-0" />
        <Text tw="my-0 text-2xl">
          <strong>92%</strong> of the youth in our programs have experienced violence at home or in
          their community
        </Text>
      </div>
    </OverlapImage>
    <Container>
      <Heading id={toc[1].id} level="h2">
        <span tw="text-gray-900">Youth</span> Outreach Workers
      </Heading>
      <Heading level="h3">Unlocking potential</Heading>
      <Callout>
        <Text tw="my-0 text-2xl">
          Youth Outreach Workers like Stacy help young people access opportunities for personal
          growth
        </Text>
      </Callout>
      <Text>
        Linda was at her wits’ end. A young mother with a one-year old daughter, Linda was
        struggling with motherhood, the pandemic, and the loss of her father. She reached out to The
        Neighbourhood Group for support, and found the help she needed.
      </Text>
      <Text>
        Provincial Youth Outreach Workers (YOWs) help young people like Linda. By working with
        schools, community housing, hospitals, primary and mental health care facilities and
        settlement services, YOWs help youth and their families access critical community supports,
        and engage young people in meaningful opportunities for change.
      </Text>
      <Text>
        One of our newest YOWs, Stacy, knows very well what growing up without support can be like.
        “I was raised in the Taylor-Massey community. It has always been pretty rough there and I
        saw the heartbreaking effect that life without resources and experiences had on some of my
        childhood friends. My mom worked hard to put me in after-school programs and summer camp.
        When I saw the work she did at Neighbourhood Link, I knew I wanted to help people like she
        did.
      </Text>
      <Blockquote>
        <Text tw="my-0">
          “As a YOW, I can help young people access the resources and experiences that many of my
          peers couldn’t. What guides me is the belief that young people are full of potential. I
          look at how far Linda has come, the sense of purpose she now has despite her challenges,
          and I know that with the right supports, opportunities and guidance, young people are
          capable of anything.”
        </Text>
      </Blockquote>
    </Container>
    <FadedImageText
      image={
        <StaticImage src="../images/women-outside-in-red-further-away.jpg" alt="" style={fsbgimg} />
      }
      textSide={'left'}
    >
      <div tw="flex flex-col items-center space-y-12">
        <Text tw="my-2 text-3xl max-w-sm">
          100% of youth surveyed in the program would refer friends to Youth Outreach Workers
        </Text>
        <Group tw="w-32 h-32 lg:(w-64 h-64)" />
      </div>
    </FadedImageText>
  </Layout>
);

export default ChildrenYouthPage;
